import React, { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Radio, ShieldCheck, VideoOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICE = [{ urls: ["stun:stun.l.google.com:19302", "stun:global.stun.twilio.com:3478"] }];

const ChaserCam = () => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [facing, setFacing] = useState("environment");
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const sessionRef = useRef(null);
  const sinceRef = useRef(null);
  const timersRef = useRef([]);

  const cleanup = () => {
    timersRef.current.forEach(clearInterval);
    timersRef.current = [];
    pcRef.current?.close();
    pcRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => cleanup, []);

  const goLive = async () => {
    setError("");
    setStatus("connecting");
    try {
      const joinResponse = await fetch(`${API}/chase/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, name }),
      });
      if (!joinResponse.ok) throw new Error(joinResponse.status === 403 ? "That access code is not valid." : "Could not join the studio.");
      const session = await joinResponse.json();
      sessionRef.current = session;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;

      const pc = new RTCPeerConnection({ iceServers: ICE });
      pcRef.current = pc;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.onicecandidate = (event) => {
        if (!event.candidate) return;
        fetch(`${API}/chase/signal`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: session.id, token: session.token, role: "chaser", payload: { candidate: event.candidate } }),
        }).catch(() => {});
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") setStatus("live");
        if (pc.connectionState === "failed") { setStatus("failed"); setError("Direct connection failed — this network needs a TURN relay."); }
        if (pc.connectionState === "disconnected") setStatus("connecting");
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await fetch(`${API}/chase/signal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.id, token: session.token, role: "chaser", payload: { sdp: pc.localDescription } }),
      });

      const poll = setInterval(async () => {
        try {
          const url = `${API}/chase/signal?session_id=${session.id}&role=chaser${sinceRef.current ? `&since=${encodeURIComponent(sinceRef.current)}` : ""}`;
          const messages = await (await fetch(url)).json();
          for (const message of messages) {
            sinceRef.current = message.created_at;
            if (message.payload?.sdp) await pc.setRemoteDescription(message.payload.sdp);
            else if (message.payload?.candidate) await pc.addIceCandidate(message.payload.candidate).catch(() => {});
          }
        } catch (_) {}
      }, 1500);
      const beat = setInterval(() => {
        fetch(`${API}/chase/heartbeat?session_id=${session.id}&token=${session.token}&state=${pcRef.current?.connectionState === "connected" ? "connected" : "waiting"}`, { method: "POST" }).catch(() => {});
      }, 20000);
      timersRef.current = [poll, beat];
      setStatus("waiting");
    } catch (err) {
      cleanup();
      setStatus("idle");
      setError(err.message || "Could not start the camera.");
    }
  };

  const stop = () => {
    cleanup();
    setStatus("idle");
  };

  const label = { idle: "Off air", connecting: "Connecting", waiting: "Waiting for studio", live: "LIVE to studio", failed: "Connection failed" }[status];
  const tone = status === "live" ? "bg-red-600 text-white" : status === "failed" ? "bg-amber-500/20 text-amber-200" : "bg-white/10 text-slate-300";

  return (
    <div className="mx-auto max-w-md px-4 pt-8" data-testid="chaser-cam-page">
      <div className="mono flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-cyan-400">
        <Camera className="h-3.5 w-3.5" /> Storm Desk field unit
      </div>
      <h1 className="display-italic sd-title-gradient mt-1 text-4xl uppercase">Chaser camera</h1>
      <p className="mt-2 text-sm text-slate-400">Enter the studio access code and go live straight into the Storm Desk Preview monitor.</p>

      <div className="sd-panel mt-6 space-y-3 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <span className={`mono px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${tone}`} data-testid="chaser-status">{label}</span>
          <button
            onClick={() => setFacing((value) => (value === "environment" ? "user" : "environment"))}
            data-testid="chaser-flip-camera-button"
            className="mono flex items-center gap-1.5 border border-white/10 px-2 py-1 text-[10px] uppercase tracking-widest text-slate-300"
          >
            <RefreshCw className="h-3 w-3" /> {facing === "environment" ? "Rear" : "Front"}
          </button>
        </div>

        <div className="relative aspect-video overflow-hidden rounded-lg bg-black">
          <video ref={videoRef} data-testid="chaser-preview-video" autoPlay playsInline muted className="h-full w-full object-cover" />
          {status === "idle" && (
            <div className="absolute inset-0 grid place-items-center text-slate-600"><VideoOff className="h-8 w-8" /></div>
          )}
        </div>

        {status === "idle" ? (
          <>
            <Input data-testid="chaser-name-input" value={name} onChange={(event) => setName(event.target.value)} placeholder="Your name" className="h-11 border-white/10 bg-black/40 text-slate-100" />
            <Input data-testid="chaser-code-input" value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} placeholder="Access code" className="mono h-11 border-white/10 bg-black/40 tracking-widest text-slate-100" />
            <Button data-testid="chaser-go-live-button" onClick={goLive} disabled={!code} className="h-12 w-full gap-2 bg-red-600 text-white hover:bg-red-500">
              <Radio className="h-4 w-4" /> Go live to studio
            </Button>
          </>
        ) : (
          <Button data-testid="chaser-stop-button" onClick={stop} variant="outline" className="h-12 w-full border-white/15 bg-white/[0.03] text-slate-100 hover:bg-white/[0.08]">
            Stop camera
          </Button>
        )}

        {error && <div data-testid="chaser-error" className="mono text-[11px] uppercase tracking-wider text-red-400">{error}</div>}
        <p className="mono flex items-start gap-1.5 text-[10px] uppercase leading-relaxed tracking-wider text-slate-600">
          <ShieldCheck className="mt-0.5 h-3 w-3 shrink-0" /> Video goes peer-to-peer to the Storm Desk studio only. Keep this screen open while live.
        </p>
      </div>
    </div>
  );
};

export default ChaserCam;
